import React from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { price, quantity } from "@/utils/format";
import type { AiUsageStatsData } from "@/pages/AiConsumption/types";

interface AiConsumptionSummaryCardsProps {
    totals: AiUsageStatsData["totals"];
}

export const AiConsumptionSummaryCards: React.FC<
    AiConsumptionSummaryCardsProps
> = ({ totals }) => {
    const { t } = useTranslation("global");

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 w-full">
            {/* Cost Card (Highlighted) */}
            <div className="p-4 bg-box relative rounded-2xl border border-primary/30 shadow-xs flex flex-col items-center justify-center text-center transition-all hover:shadow-md overflow-hidden">
                <div className="absolute inset-0 bg-primary/5 pointer-events-none"></div>
                <Icon
                    value="coins"
                    size={22}
                    className="mb-2 text-primary-500 dark:text-primary-400 relative z-10"
                />
                <div className="text-[11px] font-bold text-primary-600 dark:text-primary-400 mb-1 uppercase tracking-wider min-h-[20px] flex items-center relative z-10">
                    {t("ai_consumption.total_cost")}
                </div>
                <div
                    className="font-extrabold text-primary-700 dark:text-primary-300 text-xl truncate w-full relative z-10"
                    title={`${totals.cost}€`}
                >
                    {price(totals.cost, "euro", "0.00000€", 5)}
                </div>
            </div>

            {/* Total Tokens Card */}
            <div className="p-4 bg-box rounded-2xl border border-box shadow-xs flex flex-col items-center justify-center text-center transition-all hover:shadow-md">
                <Icon
                    value="tally-5"
                    size={22}
                    className="mb-2 text-slate-400 dark:text-zinc-500"
                />
                <div className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wider min-h-[20px] flex items-center">
                    {t("ai_consumption.total_tokens")}
                </div>
                <div className="font-extrabold text-slate-800 dark:text-zinc-200 text-xl">
                    {quantity(totals.total_tokens)}
                </div>
            </div>

            {/* In Tokens Card */}
            <div className="p-4 bg-box rounded-2xl border border-box shadow-xs flex flex-col items-center justify-center text-center transition-all hover:shadow-md">
                <Icon
                    value="arrowUp"
                    size={22}
                    className="mb-2 text-slate-400 dark:text-zinc-500"
                />
                <div className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wider min-h-[20px] flex items-center">
                    {t("ai_consumption.input_tokens")}
                </div>
                <div className="font-extrabold text-slate-800 dark:text-zinc-200 text-xl">
                    {quantity(totals.input_tokens)}
                </div>
            </div>

            {/* Out Tokens Card */}
            <div className="p-4 bg-box rounded-2xl border border-box shadow-xs flex flex-col items-center justify-center text-center transition-all hover:shadow-md">
                <Icon
                    value="arrowDown"
                    size={22}
                    className="mb-2 text-slate-400 dark:text-zinc-500"
                />
                <div className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wider min-h-[20px] flex items-center">
                    {t("ai_consumption.output_tokens")}
                </div>
                <div className="font-extrabold text-slate-800 dark:text-zinc-200 text-xl">
                    {quantity(totals.output_tokens)}
                </div>
            </div>

            {/* Requests Card */}
            <div className="p-4 bg-box rounded-2xl border border-box shadow-xs flex flex-col items-center justify-center text-center transition-all hover:shadow-md">
                <Icon
                    value="sparkles"
                    size={22}
                    className="mb-2 text-amber-500 dark:text-amber-400"
                />
                <div className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase tracking-wider min-h-[20px] flex items-center">
                    {t("ai_consumption.total_requests")}
                </div>
                <div className="font-extrabold text-slate-800 dark:text-zinc-200 text-xl">
                    {quantity(totals.requests)}
                </div>
            </div>
        </div>
    );
};
